import { uiLogger } from '../utils/loggerInstance';
import { withRetry, rateLimitCircuitBreaker } from '../utils/apiRetry';
import api from './apiService';
import { resolveChatStreamApiBase } from '../config/auth';
import {
  getCsrfTokenFromRegistry,
  getAccessTokenFromRegistry,
  registerAuthStateChangeListener,
} from './tokenRefreshRegistry';
import { getApiErrorMessage, isQuotaExceeded429Error } from '../utils/apiErrorUtils';
import { getCsrfTokenFromDocumentCookie } from '../utils/cookieUtils';
import { resolveToModelName } from '../utils/resolveModelName';
import { MODEL_NAMES, ModelName } from '../constants/modelNames';
import { getRecommendedModel } from '../constants/ModelOptions';
import { INDUSTRY_MODEL_ORDERING } from '../constants/IndustryModelOrdering';

import { DocumentChatResponse, ChatSessionItem, BackendChatMessage } from '../types/chat';
import { Industry } from '../types/industry';
import { HEARTBEAT_TIMEOUT_MS } from '../constants/chatConfig';
import { NEW_CHAT_SESSION_ID } from '../constants/ChatConstants';
import { promoteIncrementalExtractIntoResult } from '../utils/incrementalExtraction';
import { logChatFlow } from '../monitoring/datadogClient';

interface ChatSessionsResponse {
  sessions: ChatSessionItem[];
  count: number;
  retrieved_at: string;
}

interface ApiError {
  response?: {
    status?: number;
    data?: unknown;
  };
}

class StreamingApiError extends Error {
  response: { status?: number; data?: unknown };

  constructor(message: string, status?: number, data?: unknown) {
    super(message);
    this.name = 'StreamingApiError';
    this.response = { status, data };
  }
}

/**
 * Type guard to check if an error has an API response structure
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as ApiError).response === 'object' &&
    (error as ApiError).response !== null
  );
}

/**
 * Safely extract HTTP status code from an error
 */
function getErrorStatus(error: unknown): number | undefined {
  return isApiError(error) ? error.response?.status : undefined;
}

/**
 * Extract a descriptive error message from an API error
 */
function extractErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    const data = error.response?.data;
    if (data && typeof data === 'object') {
      const msg = getApiErrorMessage(data);
      if (msg) return msg;
      if ('message' in data && typeof data.message === 'string') return data.message;
    }
  }
  return error instanceof Error ? error.message : fallback;
}

function normalizeSessionsModels(sessions: ChatSessionItem[]): ChatSessionItem[] {
  return sessions.map((session) => ({
    ...session,
    model: resolveToModelName(session.model),
  }));
}

interface AbortSignalWithAny {
  any(signals: Iterable<AbortSignal>): AbortSignal;
}

export class ChatService {
  private currentUserId: string | undefined = undefined;
  private cachedAccessToken: string | null = null;

  constructor() {
    // Register for auth state changes to clear token cache
    registerAuthStateChangeListener((state) => {
      if (state === 'logged_out') {
        this.clearTokenCache();
      }
    });
  }

  /**
   * Fetch access token for cross-origin requests
   * Delegates to tokenRefreshRegistry which centralizes auth service and backend bridge
   */
  private async fetchAccessToken(): Promise<string | null> {
    try {
      const token = await getAccessTokenFromRegistry();
      if (token) {
        this.cachedAccessToken = token;
        uiLogger.debug('Access token retrieved from registry and cached for streaming');
        return token;
      }
      return null;
    } catch (error) {
      uiLogger.warn('Failed to fetch access token from registry', { error });
      return null;
    }
  }

  /**
   * Clear the cached access token
   * Used on logout or when the token is known to be invalid
   */
  clearTokenCache(): void {
    this.cachedAccessToken = null;
    uiLogger.debug('Access token cache cleared in chat service');
  }

  /**
   * Set the current user ID for the chat service
   */
  setCurrentUser(userId: string | undefined): void {
    this.currentUserId = userId;
    uiLogger.debug('Current user set in chat service', { userId: this.currentUserId });
  }

  /**
   * Set the current user ID (alias for backward compatibility)
   */
  setUserId(userId: string): void {
    this.setCurrentUser(userId);
  }

  /**
   * Set current session (stub for backward compatibility)
   */
  setCurrentSession(sessionId: string, fileId: string | null): void {
    uiLogger.debug('setCurrentSession called', { sessionId, fileId });
    // This method is kept for compatibility but doesn't need implementation
    // as session management is handled at the component level
  }

  /** Base for POST /api/v1/chat/message (may use direct Cloud Run URL for long streams). */
  private getChatMessageApiBase(): string {
    return resolveChatStreamApiBase();
  }

  /**
   * Get chat messages for a session with optional pagination.
   * Returns the most recent messages first (newest in the last page); each page is in chronological order.
   */
  async getChatMessages(
    sessionId: string,
    options?: { limit?: number; offset?: number; signal?: AbortSignal },
  ): Promise<{ messages: BackendChatMessage[]; count: number }> {
    if (sessionId === NEW_CHAT_SESSION_ID) {
      return { messages: [], count: 0 };
    }
    const limit = options?.limit ?? 20;
    const offset = options?.offset ?? 0;
    try {
      uiLogger.debug('Loading chat messages for session', { sessionId, limit, offset });

      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      const response = await api.get(
        `/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages?${params.toString()}&_t=${Date.now()}`,
        { signal: options?.signal },
      );
      const result = response.data;

      uiLogger.info('Chat messages loaded', {
        sessionId,
        messageCount: result.messages?.length || 0,
      });

      return {
        messages: result.messages || [],
        count: result.count || 0,
      };
    } catch (error) {
      const errorMessage = extractErrorMessage(error, 'Error getting chat messages');
      uiLogger.error(errorMessage, { sessionId, error });

      if (error instanceof Error) {
        throw error;
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Persist user-verified form JSON and/or per-document layout settings on an assistant message.
   */
  async patchAssistantFormJson(
    sessionId: string,
    messageId: string | number,
    payload: {
      form_json?: Record<string, unknown>;
      document_layout_settings?: Record<string, unknown>;
    },
  ): Promise<{ message_id: number; confidence_score: number | null; session_id?: string }> {
    const response = await api.patch<{
      message_id: number;
      confidence_score: number | null;
      session_id?: string;
    }>(
      `/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(String(messageId))}`,
      payload,
    );
    return response.data;
  }

  /**
   * Create a new chat session using backend API.
   * sessionId is required; the backend creates the session with this client-generated id.
   * @returns The session id (same as the one passed in when client-generated).
   */
  async createNewChatSession(
    industry: Industry,
    sessionType: string,
    sessionId: string,
    model: ModelName,
    workspaceId: string,
    enabledProviders?: string[],
  ): Promise<string> {
    const endpoint = '/api/v1/chat/sessions';

    try {
      const resolvedModel = this.resolveModel(model, industry, enabledProviders);
      const requestBody: Record<string, any> = {
        session_type: sessionType,
        industry,
        session_id: sessionId,
        model: resolvedModel,
        workspace_id: workspaceId,
      };

      const response = await api.post<{ session_id: string }>(`${endpoint}/new`, requestBody);

      uiLogger.info('New chat session created', {
        sessionId: response.data.session_id,
        workspaceId,
        industry,
        sessionType,
        model,
      });
      return response.data.session_id;
    } catch (error) {
      const errorMessage = extractErrorMessage(error, 'Error creating new chat session');
      uiLogger.error(errorMessage, { error });

      if (error instanceof Error) {
        throw error;
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Send a chat message using the unified streaming endpoint.
   * Model is explicit and required in the request.
   */
  async sendMessage(
    message: string,
    sessionId: string,
    options: {
      fileId?: string;
      model: ModelName;
      document_language: string;
      industry?: Industry | null;
      enabledProviders?: string[];
      /** Called with (sessionId, data) so concurrent streams update the correct session. */
      onUpdate?: (sessionId: string, data: any) => void;
      signal?: AbortSignal;
    },
  ): Promise<DocumentChatResponse> {
    const { fileId, model, document_language, industry, enabledProviders, onUpdate, signal } =
      options;

    const resolvedModel = this.resolveModel(model, industry ?? undefined, enabledProviders);

    const url = `${this.getChatMessageApiBase()}/api/v1/chat/message`;
    const requestBody: Record<string, unknown> = {
      message,
      session_id: sessionId,
      model: resolvedModel,
      document_language,
    };
    if (fileId) requestBody.file_id = fileId;

    let incrementalUpdateCount = 0;
    let lastIncrementalKeyCount = 0;
    const wrappedOnUpdate =
      onUpdate && typeof onUpdate === 'function'
        ? (data: unknown) => {
            incrementalUpdateCount += 1;
            lastIncrementalKeyCount =
              data && typeof data === 'object' && !Array.isArray(data)
                ? Object.keys(data).length
                : 0;
            onUpdate(sessionId, data);
          }
        : undefined;

    try {
      const result = await this.streamingFetch(url, requestBody, wrappedOnUpdate, signal);
      const assistantMessages =
        result.new_messages?.filter((message) => message.role?.toLowerCase() === 'assistant') ?? [];
      logChatFlow('stream_returned', {
        session_id: sessionId,
        incremental_update_count: incrementalUpdateCount,
        last_incremental_key_count: lastIncrementalKeyCount,
        extracted_key_count:
          result.extractedData && typeof result.extractedData === 'object'
            ? Object.keys(result.extractedData).length
            : 0,
        raw_extracted_key_count:
          result.rawExtractedData && typeof result.rawExtractedData === 'object'
            ? Object.keys(result.rawExtractedData).length
            : 0,
        response_length: result.response?.length ?? 0,
        new_message_count: result.new_messages?.length ?? 0,
        assistant_message_count: assistantMessages.length,
        assistant_has_form_json: assistantMessages.some(
          (message) => message.form_json && Object.keys(message.form_json).length > 0,
        ),
        show_form: Boolean(result.showFormWithData),
        is_form_response: Boolean(result.is_form_response),
        is_partial: Boolean(result.is_partial),
        status: result.status ?? null,
      });

      if (!result.showFormWithData && !result.extractedData) {
        const processed = this.processJsonResponse(result);
        logChatFlow('stream_result_processed_as_json', {
          session_id: sessionId,
          show_form: Boolean(processed.showFormWithData),
          extracted_key_count:
            processed.extractedData && typeof processed.extractedData === 'object'
              ? Object.keys(processed.extractedData).length
              : 0,
        });
        return processed;
      }
      return result;
    } catch (error) {
      logChatFlow(
        'stream_failed',
        {
          session_id: sessionId,
          incremental_update_count: incrementalUpdateCount,
          last_incremental_key_count: lastIncrementalKeyCount,
          error_name: error instanceof Error ? error.name : 'unknown',
        },
        'error',
      );
      const errorMessage = extractErrorMessage(error, 'Error sending message');
      uiLogger.error(errorMessage, { error });
      if (error instanceof Error) throw error;
      throw new Error(errorMessage);
    }
  }

  private getCsrfToken(): string | null {
    // 1. Try reading from cookie using shared utility
    const token = getCsrfTokenFromDocumentCookie(uiLogger);
    if (token) {
      return token;
    }

    // 2. Fallback to in-memory registry
    return getCsrfTokenFromRegistry();
  }

  /**
   * Setup authentication headers for streaming requests
   */
  private async setupAuthHeaders(url: string, headers: Record<string, string>): Promise<void> {
    const isDirectConnection = url.startsWith('http') && !url.includes(globalThis.location.host);
    if (isDirectConnection) {
      const token = this.cachedAccessToken ?? (await this.fetchAccessToken());
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        uiLogger.debug('Using Bearer token for cross-origin streaming connection');
      } else {
        uiLogger.warn('No access token available for cross-origin streaming connection');
      }
    }

    // CSRFMiddleware skips CSRF when Authorization: Bearer is set; no need to send X-CSRF-Token.
    if (headers['Authorization']?.startsWith('Bearer ')) {
      return;
    }

    const csrfToken = this.getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    } else {
      uiLogger.warn('CSRF token not found for streaming request');
    }
  }

  /**
   * Combine abort signals for timeout and external cancellation
   */
  private combineAbortSignals(controller: AbortController, signal?: AbortSignal): AbortSignal {
    if (!signal) {
      return controller.signal;
    }

    const Signal = AbortSignal as unknown as AbortSignalWithAny;
    if (typeof Signal.any === 'function') {
      return Signal.any([signal, controller.signal]);
    }

    signal.addEventListener('abort', () => controller.abort(), { once: true });
    return controller.signal;
  }

  /**
   * Extract error detail from failed response
   */
  private async extractErrorDetail(
    response: Response,
  ): Promise<{ message: string; data?: unknown }> {
    let message =
      response.status === 502 || response.status === 503 || response.status === 504
        ? 'Temporary service disruption. Please try again shortly.'
        : 'Stream request failed';
    let data: unknown;
    try {
      data = await response.json();
      const msg = getApiErrorMessage(data);
      if (msg) message = msg;
      else if (
        data != null &&
        typeof data === 'object' &&
        'message' in data &&
        typeof data.message === 'string'
      ) {
        message = data.message;
      }
    } catch (e) {
      uiLogger.debug('Failed to parse error response as JSON, using default message', {
        error: e,
        status: response.status,
      });
    }
    return { message, data };
  }

  /**
   * Process a single NDJSON chunk.
   * Truncated trailing lines (proxy cutoffs) are skipped — caller may recover via last incremental.
   */
  private processNdjsonChunk(
    line: string,
    onUpdate?: (data: unknown) => void,
  ): DocumentChatResponse | null {
    if (!line.trim()) {
      return null;
    }

    let chunk;
    try {
      chunk = JSON.parse(line);
    } catch (e) {
      uiLogger.warn('Failed to parse ndjson chunk', {
        linePreview: line.slice(0, 200),
        lineLength: line.length,
        error: e,
      });
      // Do not throw: incomplete final lines are common when a long healthcare extract is cut mid-stream.
      return null;
    }

    if (chunk.type === 'heartbeat') {
      return null;
    }

    if (chunk.type === 'incremental') {
      if (onUpdate) {
        onUpdate(chunk.data);
      }
      return null;
    }

    if (chunk.type === 'final') {
      return chunk.data;
    }

    if (chunk.type === 'error') {
      const status = typeof chunk.status_code === 'number' ? chunk.status_code : undefined;
      const errorPayload = chunk.detail ?? chunk.message;
      const data =
        errorPayload != null && typeof errorPayload === 'object'
          ? { detail: errorPayload }
          : { message: errorPayload };
      const message = getApiErrorMessage(data) || 'Streaming error';
      throw new StreamingApiError(message, status, data);
    }
    return null;
  }

  /**
   * Build a usable chat result from the last incremental extraction payload
   * when the stream ends or times out before a final chunk.
   */
  private buildPartialResultFromIncremental(data: Record<string, unknown>): DocumentChatResponse {
    return {
      user_message: '',
      response:
        'Analysis could not complete before the full document was processed. Here is the information extracted so far.',
      extractedData: data,
      rawExtractedData: data,
      showFormWithData: true,
      is_form_response: true,
      formMappingApplied: true,
      status: 'partial',
      is_partial: true,
    };
  }

  private isAbortError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }
    const err = error as { name?: string; message?: string };
    return (
      err.name === 'AbortError' ||
      (typeof err.message === 'string' && /aborted|abort/i.test(err.message))
    );
  }

  private isRecoverableStreamError(error: unknown): boolean {
    if (this.isAbortError(error)) {
      return true;
    }
    if (!error || typeof error !== 'object') {
      return false;
    }
    const err = error as { name?: string; message?: string };
    if (err.name === 'SyntaxError') {
      return true;
    }
    return (
      typeof err.message === 'string' &&
      (/Unterminated string in JSON|Unexpected end of JSON|JSON\.parse/i.test(err.message) ||
        /Failed to fetch|network error|load failed/i.test(err.message))
    );
  }

  /**
   * Process stream reader and extract final result
   */
  private async processStreamReader(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    resetTimeout: () => void,
    onUpdate?: (data: unknown) => void,
  ): Promise<DocumentChatResponse> {
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult: DocumentChatResponse | null = null;
    let lastIncremental: Record<string, unknown> | null = null;

    const trackIncremental = (data: unknown) => {
      if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
        lastIncremental = data as Record<string, unknown>;
      }
      onUpdate?.(data);
    };

    const consumeLine = (line: string) => {
      const result = this.processNdjsonChunk(line, trackIncremental);
      if (result) {
        finalResult = result;
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        resetTimeout();

        if (done) {
          buffer += decoder.decode();
          if (buffer.trim()) {
            consumeLine(buffer);
            buffer = '';
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          consumeLine(line);
        }
      }
    } catch (error) {
      if (lastIncremental && this.isRecoverableStreamError(error)) {
        uiLogger.warn('Streaming interrupted; returning partial extracted data', {
          keys: Object.keys(lastIncremental).length,
          error,
        });
        return this.buildPartialResultFromIncremental(lastIncremental);
      }
      throw error;
    } finally {
      reader.releaseLock();
    }

    if (!finalResult) {
      if (lastIncremental) {
        uiLogger.warn('Stream ended without final result; returning partial extracted data', {
          keys: Object.keys(lastIncremental).length,
        });
        return this.buildPartialResultFromIncremental(lastIncremental);
      }
      throw new Error('Stream ended without final result');
    }

    const merged = promoteIncrementalExtractIntoResult(finalResult, lastIncremental);
    if (merged !== finalResult && lastIncremental) {
      uiLogger.warn('Final stream result missing form data; promoting incremental extract', {
        incrementalKeys: Object.keys(lastIncremental).length,
      });
    }
    return merged;
  }

  /**
   * Helper to perform a streaming catch using fetch and handle ndjson chunks.
   * Internal method used by sendDocumentMessage.
   */
  private async streamingFetch(
    url: string,
    body: any,
    onUpdate?: (data: unknown) => void,
    signal?: AbortSignal,
  ): Promise<DocumentChatResponse> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
    };

    await this.setupAuthHeaders(url, headers);

    const controller = new AbortController();
    let currentTimeoutId: ReturnType<typeof setTimeout>;

    const resetTimeout = () => {
      if (currentTimeoutId) clearTimeout(currentTimeoutId);
      currentTimeoutId = setTimeout(() => {
        uiLogger.warn('Streaming fetch timed out due to inactivity', { url });
        controller.abort();
      }, HEARTBEAT_TIMEOUT_MS);
    };

    const combinedSignal = this.combineAbortSignals(controller, signal);
    resetTimeout();

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        credentials: 'include',
        signal: combinedSignal,
      });

      if (!response.ok) {
        // Clear token cache on 401 Unauthorized as it indicates the cached token is invalid
        if (response.status === 401) {
          uiLogger.warn('Streaming fetch received 401 Unauthorized, clearing token cache');
          this.clearTokenCache();
        }

        const errorDetail = await this.extractErrorDetail(response);
        throw new StreamingApiError(errorDetail.message, response.status, errorDetail.data);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body reader not available');
      }

      return await this.processStreamReader(reader, resetTimeout, onUpdate);
    } finally {
      if (currentTimeoutId!) clearTimeout(currentTimeoutId);
    }
  }

  /**
   * Process response to detect and handle JSON form data.
   * Only used for streaming message responses (DocumentChatResponse).
   * Industry is preserved from result (backend includes it in the response).
   */
  private processJsonResponse(result: DocumentChatResponse): DocumentChatResponse {
    try {
      const responseText = result.response;

      const jsonBlockMatch = responseText.match(/```json\s*(\{[\s\S]*?\})\s*```/i);
      if (jsonBlockMatch) {
        try {
          const extractedData = JSON.parse(jsonBlockMatch[1]);
          return {
            ...result,
            showFormWithData: true,
            extractedData,
            rawExtractedData: extractedData,
            formMappingApplied: true,
            is_form_response: true,
          };
        } catch (parseError) {
          uiLogger.warn('Failed to parse JSON from code block', { parseError });
        }
      }

      if (responseText.trim().startsWith('{') && responseText.trim().endsWith('}')) {
        try {
          const extractedData = JSON.parse(responseText.trim());
          return {
            ...result,
            showFormWithData: true,
            extractedData,
            rawExtractedData: extractedData,
            formMappingApplied: true,
            is_form_response: true,
          };
        } catch (parseError) {
          uiLogger.warn('Failed to parse standalone JSON', { parseError });
        }
      }

      const formKeywords = [
        'form',
        'template',
        'fields',
        'banking',
        'healthcare',
        'insurance',
        'legal',
        'retail',
      ];
      const containsFormKeywords = formKeywords.some((keyword) =>
        responseText.toLowerCase().includes(keyword),
      );

      if (containsFormKeywords) {
        return { ...result, is_form_response: true };
      }

      return result;
    } catch (error) {
      uiLogger.error('Error processing JSON response', { error });
      return result;
    }
  }

  /**
   * Get chat sessions from the backend
   */
  async getChatSessions(
    limit: number = 5,
    offset: number = 0,
    search?: string,
  ): Promise<ChatSessionsResponse> {
    const endpoint = '/api/v1/chat/sessions';

    // Check circuit breaker
    if (rateLimitCircuitBreaker.isOpen(endpoint)) {
      uiLogger.warn('Circuit breaker is open for chat sessions, returning empty result');
      return { sessions: [], count: 0, retrieved_at: new Date().toISOString() };
    }

    try {
      const result = await withRetry(
        async () => {
          const params = this.buildSessionsQueryParams(limit, offset, search);
          const response = await api.get(`${endpoint}?${params.toString()}`);
          const result = response.data;

          uiLogger.info('Chat sessions loaded', {
            sessionCount: result.sessions?.length || 0,
            userId: this.currentUserId || 'anonymous',
            limit,
            offset,
            hasSearch: !!search,
          });

          return {
            ...result,
            sessions: normalizeSessionsModels(result.sessions || []),
          };
        },
        {
          maxRetries: 2,
          baseDelay: 2000, // Start with 2 seconds for rate limits
          maxDelay: 60000, // Max 60 seconds
          shouldRetry: (error: unknown) => {
            const status = getErrorStatus(error);
            if (status === 429 && isQuotaExceeded429Error(error)) {
              uiLogger.warn('Quota/plan 429 from sessions API — not retrying as rate limit');
              return false;
            }
            // Only retry on 429 (rate limit) and 5xx errors
            if (status === 429) {
              uiLogger.warn('Rate limit hit, will retry with backoff');
              return true;
            }
            return status !== undefined && status >= 500;
          },
        },
        (attempt, delay, error) => {
          const status = getErrorStatus(error);
          if (status === 429) {
            uiLogger.info(`Rate limit retry ${attempt} for chat sessions in ${delay}ms`, {
              attempt,
              delay,
            });
          } else {
            uiLogger.info(`Retrying chat sessions in ${delay}ms (attempt ${attempt})`, {
              attempt,
              delay,
              status,
            });
          }
        },
      );

      // Record success
      rateLimitCircuitBreaker.recordSuccess(endpoint);
      return result;
    } catch (error: unknown) {
      // Record failure for rate limit errors
      if (getErrorStatus(error) === 429) {
        rateLimitCircuitBreaker.recordFailure(endpoint);
      }
      throw error;
    }
  }

  /**
   * Get workspace-scoped chat sessions from the backend
   */
  async getWorkspaceSessions(
    workspaceId: string,
    limit: number = 5,
    offset: number = 0,
    search?: string,
  ): Promise<ChatSessionsResponse> {
    const endpoint = `/api/v1/chat/sessions/workspace/${workspaceId}`;

    // Check circuit breaker
    if (rateLimitCircuitBreaker.isOpen(endpoint)) {
      uiLogger.warn('Circuit breaker is open for workspace sessions, returning empty result', {
        workspaceId,
      });
      return { sessions: [], count: 0, retrieved_at: new Date().toISOString() };
    }

    try {
      const result = await withRetry(
        async () => {
          const params = this.buildSessionsQueryParams(limit, offset, search);
          const response = await api.get(`${endpoint}?${params.toString()}`);
          const result = response.data;

          uiLogger.info('Workspace chat sessions loaded', {
            workspaceId,
            sessionCount: result.sessions?.length || 0,
            userId: this.currentUserId || 'anonymous',
            limit,
            offset,
            hasSearch: !!search,
          });

          return {
            ...result,
            sessions: normalizeSessionsModels(result.sessions || []),
          };
        },
        {
          maxRetries: 2,
          baseDelay: 2000, // Start with 2 seconds for rate limits
          maxDelay: 60000, // Max 60 seconds
          shouldRetry: (error: unknown) => {
            const status = getErrorStatus(error);
            if (status === 429 && isQuotaExceeded429Error(error)) {
              uiLogger.warn(
                'Quota/plan 429 from workspace sessions API — not retrying as rate limit',
                {
                  workspaceId,
                },
              );
              return false;
            }
            // Only retry on 429 (rate limit) and 5xx errors, with longer delays for rate limits
            if (status === 429) {
              uiLogger.warn('Rate limit hit, will retry with backoff', { workspaceId });
              return true;
            }
            return status !== undefined && status >= 500;
          },
        },
        (attempt, delay, error) => {
          const status = getErrorStatus(error);
          if (status === 429) {
            uiLogger.info(`Rate limit retry ${attempt} for workspace sessions in ${delay}ms`, {
              workspaceId,
              attempt,
              delay,
            });
          } else {
            uiLogger.info(`Retrying workspace sessions in ${delay}ms (attempt ${attempt})`, {
              workspaceId,
              attempt,
              delay,
              status,
            });
          }
        },
      );

      // Record success
      rateLimitCircuitBreaker.recordSuccess(endpoint);
      return result;
    } catch (error: unknown) {
      // Record failure for rate limit errors
      if (getErrorStatus(error) === 429) {
        rateLimitCircuitBreaker.recordFailure(endpoint);
      }
      throw error;
    }
  }

  /**
   * Delete a chat session
   */
  async deleteSession(sessionId: string): Promise<{ success: boolean; message: string }> {
    try {
      uiLogger.debug('Deleting chat session', { sessionId });

      const response = await api.delete(`/api/v1/chat/sessions/${encodeURIComponent(sessionId)}`);
      const result = response.data;

      uiLogger.info('Chat session deleted successfully', { sessionId });
      return { success: true, message: result.message || 'Session deleted successfully' };
    } catch (error) {
      const errorMessage = extractErrorMessage(error, 'Failed to delete session');
      uiLogger.error('Error deleting chat session', { sessionId, error, errorMessage });
      throw new Error(errorMessage);
    }
  }

  // ===== PRIVATE HELPER METHODS =====

  /**
   * Resolve the model name - converts "Recommended Model" to the actual model.
   * @throws Error if model is empty string, or if "Recommended Model" is selected but no models are available
   */
  private resolveModel(
    model: ModelName,
    industry?: Industry,
    enabledProviders?: string[],
  ): ModelName {
    if (!model.trim()) {
      throw new Error('Model is required');
    }
    if (model === MODEL_NAMES.RECOMMENDED) {
      const customOrder = industry ? INDUSTRY_MODEL_ORDERING[industry] : undefined;
      return getRecommendedModel(enabledProviders, customOrder);
    }
    return model;
  }

  /**
   * Sanitize and validate search parameter to prevent abuse
   * @param search - Raw search input
   * @returns Sanitized search string or undefined if invalid
   */
  private sanitizeSearchParameter(search: string | undefined): {
    sanitized: string | undefined;
    wasTruncated: boolean;
  } {
    if (!search) {
      return { sanitized: undefined, wasTruncated: false };
    }

    // Trim whitespace
    let sanitized = search.trim();

    // Return undefined for empty strings after trimming
    if (sanitized.length === 0) {
      return { sanitized: undefined, wasTruncated: false };
    }

    // Max length limit (200 characters - reasonable for search queries)
    // Prevents DoS attacks via extremely long search strings
    const MAX_SEARCH_LENGTH = 200;
    let wasTruncated = false;

    if (sanitized.length > MAX_SEARCH_LENGTH) {
      uiLogger.warn('Search parameter exceeded max length, truncating', {
        originalLength: sanitized.length,
        maxLength: MAX_SEARCH_LENGTH,
      });
      sanitized = sanitized.substring(0, MAX_SEARCH_LENGTH);
      wasTruncated = true;
    }

    // Remove control characters (0x00-0x1F, 0x7F) that could cause issues
    // Keep newline (\n), tab (\t), and carriage return (\r) for multi-line searches
    // but remove other control characters
    // Filter out control characters using character code comparison to avoid ESLint no-control-regex rule
    sanitized = sanitized
      .split('')
      .filter((char) => {
        const code = char.codePointAt(0);
        if (code === undefined) return false;
        // Keep printable characters (32-126), newline (10), tab (9), carriage return (13)
        // Remove other control characters (0-8, 11, 12, 14-31, 127)
        return (
          (code >= 32 && code <= 126) ||
          code === 9 || // tab
          code === 10 || // newline
          code === 13 || // carriage return
          code > 127 // Unicode characters above ASCII
        );
      })
      .join('');

    // Note: Null bytes (char code 0) are already removed by the filter above
    // URLSearchParams will handle URL encoding for remaining characters

    // Normalize excessive whitespace (replace multiple consecutive spaces/newlines with single space)
    // This prevents abuse via extremely long whitespace strings
    sanitized = sanitized.replace(/\s+/g, ' ');

    // Final trim after sanitization
    sanitized = sanitized.trim();

    // Return undefined if empty after sanitization
    if (sanitized.length === 0) {
      return { sanitized: undefined, wasTruncated };
    }

    return { sanitized, wasTruncated };
  }

  /**
   * Build query parameters for sessions requests
   */
  private buildSessionsQueryParams(
    limit: number,
    offset: number = 0,
    search?: string,
  ): URLSearchParams {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (this.currentUserId) {
      params.append('user_id', this.currentUserId);
    }

    // Add search parameter if provided (for future server-side search support)
    // Sanitize and validate before appending to prevent abuse
    const { sanitized: sanitizedSearch, wasTruncated } = this.sanitizeSearchParameter(search);

    // Log truncation for monitoring (could be extended to notify UI components)
    if (wasTruncated) {
      uiLogger.info('Search query was truncated due to length limit', {
        originalLength: search?.length,
        truncatedTo: sanitizedSearch?.length,
      });
    }

    if (sanitizedSearch) {
      params.append('search', sanitizedSearch);
    }

    return params;
  }

  /**
   * Public method to sanitize search input with truncation feedback
   * Useful for UI components that want to show user feedback when search is truncated
   */
  public sanitizeSearchInput(search: string | undefined): {
    sanitized: string | undefined;
    wasTruncated: boolean;
  } {
    return this.sanitizeSearchParameter(search);
  }
}

export const chatService = new ChatService();
export default ChatService;
